import { Action, DatabaseAction, ExpandedAction } from "../business-logic/ActionBehavior/Action";
import { ActionConfig } from "../business-logic/ActionBehavior/ActionConfig/ActionConfig";
import { AlwaysValidActionConfig } from "../business-logic/ActionBehavior/ActionConfig/AlwaysValidActionConfig";
import { DynamicPossessionActionConfig } from "../business-logic/ActionBehavior/ActionConfig/DynamicPossessionActionConfig";
import { KickoffActionConfig } from "../business-logic/ActionBehavior/ActionConfig/KickoffActionConfig";
import { LineoutActionConfig } from "../business-logic/ActionBehavior/ActionConfig/LineoutActionConfig";
import { MultiActionConfig } from "../business-logic/ActionBehavior/ActionConfig/MultiActionConfig";
import { OverrideActionConfig } from "../business-logic/ActionBehavior/ActionConfig/NonDatabaseActionActionConfig/OverrideActionConfig";
import { PermanentOverrideActionConfig } from "../business-logic/ActionBehavior/ActionConfig/NonDatabaseActionActionConfig/PermanentOverrideActionConfig";
import { PossessionChangeActionConfig } from "../business-logic/ActionBehavior/ActionConfig/NonDatabaseActionActionConfig/PossessionChangeActionConfig";
import { SingleActionConfig } from "../business-logic/ActionBehavior/ActionConfig/SingleActionConfig";
import { TurnoverActionConfig } from "../business-logic/ActionBehavior/ActionConfig/TurnoverActionConfig";
import {
  databaseActionsMap,
  openKickActions,
  openPlayActions,
  pausePieceActions,
} from "./actionMappingConstants";

const DATABASE_ACTION_CONFIGS: Record<DatabaseAction, SingleActionConfig> =
  //  first loop through each `actionMapping` object
  databaseActionsMap.reduce((accumulator, actionMapping) => {
    // remove `previous` property from object
    const { previous, ...actionMappingValue } = actionMapping;

    // penalty action is always possible
    actionMappingValue.next = [...actionMappingValue.next, DatabaseAction.penaltyWon];

    // then loop through each previous action in each `actionMapping` object
    const newMappings = previous.reduce((_accumulator, action) => {
      return {
        ..._accumulator,
        [action]: new SingleActionConfig(
          action,
          actionMappingValue.next,
          actionMappingValue.requiresPitchClick,
          actionMappingValue.possessionChange
        ),
      };
    }, {} as Record<DatabaseAction, SingleActionConfig>);

    const duplicateKeys = Object.keys(newMappings).filter((key) =>
      Object.keys(accumulator).includes(key)
    );
    if (duplicateKeys.length > 0) {
      console.error(
        `Duplicate actionMapping keys in previousValues: ${duplicateKeys
          .sort()
          .map((key) => DatabaseAction[key as keyof typeof DatabaseAction])}`
      );
    }
    return { ...accumulator, ...newMappings };
  }, {} as Record<DatabaseAction, SingleActionConfig>);

DATABASE_ACTION_CONFIGS[DatabaseAction.turnover] = new TurnoverActionConfig(
  DatabaseAction.turnover,
  [
    ...openPlayActions,
    ...pausePieceActions,
    DatabaseAction.kickoff,
    DatabaseAction.lineout,
    DatabaseAction.scrum,
  ]
);

DATABASE_ACTION_CONFIGS[DatabaseAction.lineout] = new LineoutActionConfig(
  DatabaseAction.lineout,
  pausePieceActions,
  true
);

DATABASE_ACTION_CONFIGS[DatabaseAction.kickoff] = new KickoffActionConfig(
  DatabaseAction.kickoff,
  [...openKickActions, DatabaseAction.freeKickWon],
  true
);

// The value for nextActions does not matter, as we should ignore flag actions in isValid() to go to the previous event.
DATABASE_ACTION_CONFIGS[DatabaseAction.flag] = new AlwaysValidActionConfig(
  DatabaseAction.flag,
  [],
  true
);

export const EXPANDED_ACTION_CONFIGS: Record<ExpandedAction, ActionConfig> = {
  [ExpandedAction.homeTeamPenalty]: new DynamicPossessionActionConfig(
    ExpandedAction.homeTeamPenalty,
    DATABASE_ACTION_CONFIGS[DatabaseAction.penaltyWon],
    "PK Won",
    true
  ),
  [ExpandedAction.awayTeamPenalty]: new DynamicPossessionActionConfig(
    ExpandedAction.awayTeamPenalty,
    DATABASE_ACTION_CONFIGS[DatabaseAction.penaltyWon],
    "PK Won",
    false
  ),
  [ExpandedAction.homeTeamFreeKick]: new DynamicPossessionActionConfig(
    ExpandedAction.homeTeamFreeKick,
    DATABASE_ACTION_CONFIGS[DatabaseAction.freeKickWon],
    "FK Won",
    true
  ),
  [ExpandedAction.awayTeamFreeKick]: new DynamicPossessionActionConfig(
    ExpandedAction.awayTeamFreeKick,
    DATABASE_ACTION_CONFIGS[DatabaseAction.freeKickWon],
    "FK Won",
    false
  ),

  [ExpandedAction.homeTeamKnockOn]: new DynamicPossessionActionConfig(
    ExpandedAction.homeTeamKnockOn,
    DATABASE_ACTION_CONFIGS[DatabaseAction.knockOn],
    "Knock On",
    true
  ),
  [ExpandedAction.awayTeamKnockOn]: new DynamicPossessionActionConfig(
    ExpandedAction.awayTeamKnockOn,
    DATABASE_ACTION_CONFIGS[DatabaseAction.knockOn],
    "Knock On",
    false
  ),

  [ExpandedAction.tackledAndRuck]: new MultiActionConfig(ExpandedAction.tackledAndRuck, [
    DATABASE_ACTION_CONFIGS[DatabaseAction.tackled],
    DATABASE_ACTION_CONFIGS[DatabaseAction.ruck],
  ]),
  [ExpandedAction.possessionChange]: new PossessionChangeActionConfig(),
  [ExpandedAction.override]: new OverrideActionConfig(),
  [ExpandedAction.permanentOverride]: new PermanentOverrideActionConfig(),
};

export const ACTION_CONFIGS: Record<Action, ActionConfig> = {
  ...DATABASE_ACTION_CONFIGS,
  ...EXPANDED_ACTION_CONFIGS,
};

const potentialStateActions = Object.values(DATABASE_ACTION_CONFIGS).reduce(
  (accumulator, actionMappingValue) => {
    actionMappingValue.nextActions?.forEach((action) =>
      action in DatabaseAction ? accumulator.add(action as DatabaseAction) : null
    );
    return accumulator;
  },
  new Set<DatabaseAction>()
);
const missingKeys = Array.from(potentialStateActions).filter(
  (id) => !Object.keys(DATABASE_ACTION_CONFIGS).includes(String(id))
);

if (missingKeys.length > 0) {
  console.error(
    `Missing previous key for values in next: ${missingKeys
      .sort()
      .map((key) => DatabaseAction[key])}`
  );
}
